/**
 * StorageImage.tsx — render an image that lives in Supabase Storage.
 *
 * WHY THIS EXISTS. Migration 0170 scoped the storage buckets to the caller, and
 * 0171 will flip `record-avatars` from public to private. A stored image value is
 * a PATH from 2026-08-18 onward (and a legacy public URL for the 14 rows written
 * before that); neither is something a browser can load directly once the bucket
 * is private. The URL is a short-lived signed capability that must be minted at
 * RENDER time and never written to a row.
 *
 * Several render sites sit inside `.map()` loops, where a hook cannot be called
 * per item. This component exists so each rendered image is its own component
 * instance with its own hook — that is the whole reason it is a component rather
 * than a bare `useSignedImage` call at every site.
 *
 * BEHAVIOUR WORTH KNOWING:
 *   • While the first mint is in flight, `fallback` renders (usually initials).
 *     The image appears a beat later. That is a real, visible change from the
 *     public-URL behaviour and is the price of a private bucket.
 *   • An EXTERNAL url — a gravatar, or a MinIO link from the Phase-2 backend
 *     bridge — is passed straight through, never signed. See `storagePathOf`.
 *   • Mints are cached by path, so the same avatar rendered forty times in a
 *     roster costs one request, not forty.
 */

import React from 'react';
import { AVATAR_BUCKET, useSignedUrl } from '../../lib/storage';

type ImgProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'>;

export interface StorageImageProps extends ImgProps {
  /** The stored value: a storage path, a legacy public URL, or an external URL. */
  value: string | null | undefined;
  /** Which bucket the path lives in. Defaults to record-avatars. */
  bucket?: string;
  /** Rendered while the URL is being minted, or when there is no image at all. */
  fallback?: React.ReactNode;
}

export function StorageImage({
  value,
  bucket = AVATAR_BUCKET,
  fallback = null,
  alt = '',
  ...imgProps
}: StorageImageProps): React.ReactElement | null {
  const src = useSignedUrl(bucket, value);
  if (!src) return <>{fallback}</>;
  // `alt` is destructured rather than spread so jsx-a11y can see it. CI=true
  // turns lint warnings into build failures here, so this is not cosmetic.
  return <img src={src} alt={alt} {...imgProps} />;
}

export default StorageImage;
